// BarcodeScanner.jsx — Camera-based barcode/QR scanner hooked to rental inventory
import React, { useEffect, useRef, useState } from 'react';

const C = { bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0', muted:'#64748b', accent:'#f97316', green:'#4ade80', red:'#f87171' };

export default function BarcodeScanner({ items = [], onFound, onRawScan, onClose }) {
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const intervalRef = useRef(null);
  const manualRef   = useRef(null);
  const toastTimer  = useRef(null);
  const [status,  setStatus]  = useState('starting'); // starting | scanning | found | error | manual
  const [result,  setResult]  = useState(null);
  const [manual,  setManual]  = useState('');
  const [facingMode, setFacingMode] = useState('environment');
  const [toast, setToast] = useState(null); // { name } — brief confirmation after add

  // Auto-focus the manual input on mount and whenever status returns to scanning
  useEffect(() => {
    if (status === 'scanning' || status === 'manual' || status === 'error') {
      setTimeout(() => manualRef.current?.focus(), 50);
    }
  }, [status]);

  useEffect(() => { startCamera(); return () => stopCamera(); }, [facingMode]);

  async function startCamera() {
    stopCamera();
    setStatus('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode, width:{ ideal:1280 }, height:{ ideal:720 } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setStatus('scanning');
      startScanning();
    } catch(e) {
      console.error('Camera error:', e);
      setStatus('error');
    }
  }

  function stopCamera() {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function startScanning() {
    // Use BarcodeDetector API if available (Chrome/Android)
    if ('BarcodeDetector' in window) {
      const detector = new window.BarcodeDetector({ formats: ['qr_code','code_128','code_39','ean_13','ean_8','upc_a','upc_e','data_matrix','aztec'] });
      intervalRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length > 0) handleScan(codes[0].rawValue);
        } catch(e) {}
      }, 300);
    } else {
      // Fallback: show manual entry hint
      setStatus('manual');
    }
  }

  function handleScan(rawValue) {
    // Raw scan mode — return value directly without matching
    if (onRawScan) {
      onRawScan(rawValue);
      close();
      return;
    }

    // Try to match against inventory by SKU, id, or name
    const match = items.find(i =>
      String(i.id) === rawValue ||
      (i.sku && i.sku.toLowerCase() === rawValue.toLowerCase()) ||
      (i.barcode && i.barcode === rawValue) ||
      i.name.toLowerCase() === rawValue.toLowerCase()
    );

    if (match) {
      // Multi-scan: add item, show toast, stay open
      onFound(match);
      showToast(match.name);
      setManual('');
      setResult(null);
      // Re-focus input for next scan
      setTimeout(() => manualRef.current?.focus(), 50);
    } else {
      // No match — show the "not found" result screen
      clearInterval(intervalRef.current);
      setStatus('found');
      setResult({ raw: rawValue, item: null });
    }
  }

  function showToast(name) {
    clearTimeout(toastTimer.current);
    setToast({ name });
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }

  function manualLookup() {
    if (!manual.trim()) return;
    handleScan(manual.trim());
  }

  function retry() {
    setResult(null);
    setManual('');
    startCamera();
  }

  function close() {
    stopCamera();
    clearTimeout(toastTimer.current);
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:C.card, borderRadius:16, border:`1px solid ${C.border}`, width:'100%', maxWidth:460, overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:800, fontSize:15, color:C.text }}>📷 Scan Barcode / QR Code</div>
          <button onClick={close} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:20 }}>✕</button>
        </div>

        {/* Toast — brief confirmation after each successful scan */}
        {toast && (
          <div style={{ padding:'8px 20px', background:'rgba(74,222,128,0.1)', borderBottom:`1px solid ${C.green}33`, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14 }}>✅</span>
            <span style={{ fontSize:12, color:C.green, fontWeight:600 }}>Added: {toast.name}</span>
          </div>
        )}

        {/* Camera view */}
        {status !== 'found' && (
          <div style={{ position:'relative', background:'#000', aspectRatio:'4/3', maxHeight:300, overflow:'hidden' }}>
            <video ref={videoRef} style={{ width:'100%', height:'100%', objectFit:'cover' }} playsInline muted />
            {/* Scan overlay */}
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
              <div style={{ width:200, height:140, border:'2px solid #f97316', borderRadius:8, boxShadow:'0 0 0 2000px rgba(0,0,0,0.4)' }} />
            </div>
            {status === 'scanning' && (
              <div style={{ position:'absolute', bottom:10, left:0, right:0, textAlign:'center', fontSize:12, color:'rgba(255,255,255,0.7)' }}>
                Point camera at barcode or QR code
              </div>
            )}
            {status === 'starting' && (
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.5)' }}>
                <div style={{ color:C.muted, fontSize:13 }}>Starting camera...</div>
              </div>
            )}
            {/* Flip camera */}
            <button onClick={()=>setFacingMode(f=>f==='environment'?'user':'environment')}
              style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.5)', border:'none', color:'#fff', borderRadius:6, padding:'4px 8px', cursor:'pointer', fontSize:13 }}>
              🔄
            </button>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div style={{ padding:'20px', textAlign:'center' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📷</div>
            <div style={{ color:C.red, fontWeight:700, marginBottom:8 }}>Camera not available</div>
            <div style={{ color:C.muted, fontSize:12 }}>Use manual entry below</div>
          </div>
        )}

        {/* Manual entry (fallback or always-available) */}
        {(status === 'scanning' || status === 'manual' || status === 'error') && (
          <div style={{ padding:'12px 16px', borderTop:`1px solid ${C.border}` }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Scan with HID scanner or type SKU / Item ID:</div>
            <div style={{ display:'flex', gap:8 }}>
              <input ref={manualRef} autoFocus
                style={{ flex:1, padding:'8px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:'#1e2235', color:C.text, fontSize:13, outline:'none' }}
                placeholder="SKU, barcode, or item ID..." value={manual} onChange={e=>setManual(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&manualLookup()} />
              <button onClick={manualLookup} style={{ padding:'8px 14px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                Look up
              </button>
            </div>
          </div>
        )}

        {/* Not found result */}
        {status === 'found' && result && !result.item && (
          <div style={{ padding:20 }}>
            <div style={{ textAlign:'center', padding:'12px 0', marginBottom:16 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>❓</div>
              <div style={{ fontWeight:700, color:C.text, marginBottom:4 }}>No match found</div>
              <div style={{ fontSize:12, color:C.muted }}>Scanned: <code style={{ color:'#fbbf24' }}>{result.raw}</code></div>
              <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>Item not in inventory or SKU not set</div>
            </div>
            <button onClick={retry} style={{ width:'100%', padding:'10px 0', borderRadius:8, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:13 }}>
              Try Again
            </button>
          </div>
        )}

        {/* Footer with close */}
        {status !== 'found' && (
          <div style={{ padding:'10px 16px', borderTop:`1px solid ${C.border}`, textAlign:'center' }}>
            <button onClick={close} style={{ padding:'8px 20px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:12, fontWeight:600 }}>
              Done Scanning
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
